/**
 * Locomotion: teleport (arc from the pointer, trigger or pinch while aiming at the floor, or
 * right thumbstick forward), snap turn 30 degrees, optional smooth locomotion on the left
 * stick, vignette on movement, seated-height recalibration. Desktop: WASD + mouse look, Q/E
 * snap turn, R recalibrate, Shift to run.
 */
import * as THREE from 'three';
import type { InputManager, Pointer } from './InputManager.ts';
import type { PlayerRig } from './PlayerRig.ts';
import type { Vignette } from './Vignette.ts';

export interface WalkableBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Optional blocked rectangles (furniture) the feet may not enter. */
  blocked?: { minX: number; maxX: number; minZ: number; maxZ: number }[];
  /** Optional round floor (the lobby hall): the feet stay within this circle too. */
  circle?: { x: number; z: number; r: number };
  /**
   * Optional floor outline (the study room's curved hall with its studio and ward room): the
   * feet stay inside it, at least `margin` from its walls.
   */
  polygon?: { points: { x: number; z: number }[]; margin: number };
}

/**
 * Moves a point inside a polygon, at least `margin` from its edges: a point outside or too
 * close goes to the nearest edge point, then `margin` inward along the edge's normal.
 * Concave corners may need a couple of passes; three are always enough here.
 */
export function clampToPolygon(
  p: { x: number; z: number },
  poly: { x: number; z: number }[],
  margin: number,
): { x: number; z: number } {
  let q = { x: p.x, z: p.z };
  for (let pass = 0; pass < 3; pass++) {
    let best = Infinity;
    let near = q;
    let normal = { x: 0, z: 0 };
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const a = poly[j]!;
      const b = poly[i]!;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len2 = dx * dx + dz * dz || 1;
      const t = Math.max(0, Math.min(1, ((q.x - a.x) * dx + (q.z - a.z) * dz) / len2));
      const nx = a.x + dx * t;
      const nz = a.z + dz * t;
      const d = Math.hypot(q.x - nx, q.z - nz);
      if (d < best) {
        best = d;
        near = { x: nx, z: nz };
        const len = Math.sqrt(len2);
        normal = { x: -dz / len, z: dx / len };
      }
    }
    const inside = insidePoly(q, poly);
    if (inside && best >= margin - 1e-6) return q;
    // The normal pointing into the polygon: test a step along it.
    const probe = { x: near.x + normal.x * 1e-3, z: near.z + normal.z * 1e-3 };
    const sign = insidePoly(probe, poly) ? 1 : -1;
    q = { x: near.x + normal.x * sign * margin, z: near.z + normal.z * sign * margin };
  }
  return q;
}

function insidePoly(p: { x: number; z: number }, poly: { x: number; z: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x)
      inside = !inside;
  }
  return inside;
}

export interface LocomotionOptions {
  smoothLocomotion: boolean;
  snapAngleDeg: number;
  walkSpeed: number;
  onTeleport?: () => void;
  onSnap?: () => void;
  onRecalibrate?: (offset: number) => void;
  /** Seated mode switched on or off by itself (see `updateSeated`). */
  onSeated?: (seated: boolean) => void;
}

/**
 * Automatic seated mode (2026-10-05, headset: a seated user showed as a child-sized avatar).
 * A headset held this low for this long is a seated user: the rig lifts them to standing eye
 * height, as R or B/Y does by hand. Standing up again (well above standing height) undoes it.
 */
export const SEATED = { below: 1.3, afterS: 3, standAbove: 1.95, standAfterS: 2 };

const SNAP_THRESHOLD = 0.7;
const TELEPORT_MAX_DISTANCE = 9;
const GRAVITY = -9.8;

/** Radians of turn per pixel of mouse movement. */
const MOUSE_SENSITIVITY = 0.0022;

export class Locomotion {
  readonly options: LocomotionOptions;
  bounds: WalkableBounds = { minX: -100, maxX: 100, minZ: -100, maxZ: 100 };
  /** Meshes the teleport arc lands on. */
  floors: THREE.Object3D[] = [];
  /** When true, pointer selects are consumed by UI and must not teleport. */
  private uiCaptured = new Set<string>();
  private readonly arc: THREE.Line;
  private readonly marker: THREE.Mesh;
  private aiming: Pointer | undefined;
  private aimTarget: THREE.Vector3 | undefined;
  private snapArmed = new Map<string, boolean>();
  private stickAiming = new Map<string, boolean>();
  private readonly raycaster = new THREE.Raycaster();
  private readonly tmp = new THREE.Vector3();
  private readonly tmpDir = new THREE.Vector3();
  private recalibrateArmed = new Map<string, boolean>();
  /** True while seated mode was switched on automatically. */
  autoSeated = false;
  private lowFor = 0;
  private highFor = 0;
  private moving = 0;
  enabled = true;

  /** True while smooth locomotion (stick or WASD) is moving the rig. */
  get isMoving(): boolean {
    return this.moving > 0.05;
  }

  constructor(
    private readonly rig: PlayerRig,
    private readonly input: InputManager,
    private readonly vignette: Vignette,
    scene: THREE.Scene,
    options?: Partial<LocomotionOptions>,
  ) {
    this.options = { smoothLocomotion: false, snapAngleDeg: 30, walkSpeed: 2.6, ...options };
    const arcGeometry = new THREE.BufferGeometry().setFromPoints(
      Array.from({ length: 24 }, () => new THREE.Vector3()),
    );
    this.arc = new THREE.Line(
      arcGeometry,
      new THREE.LineBasicMaterial({ color: 0x35b5b0, transparent: true, opacity: 0.85 }),
    );
    this.arc.visible = false;
    this.arc.frustumCulled = false;
    this.marker = new THREE.Mesh(
      new THREE.RingGeometry(0.18, 0.26, 32),
      new THREE.MeshBasicMaterial({
        color: 0x35b5b0,
        transparent: true,
        opacity: 0.85,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    this.marker.rotation.x = -Math.PI / 2;
    this.marker.visible = false;
    scene.add(this.arc, this.marker);

    input.on('selectstart', (p) => this.onSelectStart(p));
    input.on('selectend', (p) => this.onSelectEnd(p));
    input.onKey((e) => this.onKey(e));
  }

  /** The Interactive system calls this when a pointer's select was consumed by UI. */
  captureSelect(pointer: Pointer, captured: boolean): void {
    if (captured) this.uiCaptured.add(pointer.id);
    else this.uiCaptured.delete(pointer.id);
  }

  private onSelectStart(p: Pointer): void {
    if (!this.enabled || p.kind === 'mouse' || this.uiCaptured.has(p.id)) return;
    // Something interactive under the ray that sits in front of the floor hit (a stage object,
    // a token on the table) takes the trigger; only a clear ray to the floor starts a teleport.
    const floor = this.floorDistance(p);
    if (p.hitDistance !== undefined && (floor === undefined || p.hitDistance < floor - 0.05))
      return;
    if (floor === undefined) return;
    this.startAim(p);
  }

  private onSelectEnd(p: Pointer): void {
    if (this.aiming === p) this.commitTeleport();
  }

  private floorDistance(p: Pointer): number | undefined {
    this.raycaster.set(p.ray.origin, p.ray.direction);
    this.raycaster.far = TELEPORT_MAX_DISTANCE * 2;
    return this.raycaster.intersectObjects(this.floors, true)[0]?.distance;
  }

  private startAim(p: Pointer): void {
    this.aiming = p;
    this.aimTarget = undefined;
    this.input.setRayVisible(p, false);
  }

  private cancelAim(): void {
    if (this.aiming) this.input.setRayVisible(this.aiming, true);
    this.aiming = undefined;
    this.aimTarget = undefined;
    this.arc.visible = false;
    this.marker.visible = false;
  }

  private commitTeleport(): void {
    const target = this.aimTarget;
    this.cancelAim();
    if (!target) return;
    this.teleport(target);
  }

  teleport(target: THREE.Vector3): void {
    const clamped = this.clampToBounds(target.clone());
    this.rig.teleportTo(new THREE.Vector3(clamped.x, this.rig.heightOffset, clamped.z));
    this.vignette.pulse(0.9);
    this.options.onTeleport?.();
  }

  snapTurn(direction: 1 | -1): void {
    this.rig.rotateAroundHead(-direction * THREE.MathUtils.degToRad(this.options.snapAngleDeg));
    this.vignette.pulse(0.7);
    this.options.onSnap?.();
  }

  recalibrate(): void {
    const offset = this.rig.recalibrateHeight();
    this.options.onRecalibrate?.(offset);
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.enabled) return;
    // Typing into a DOM input or a world-space text field: Q, E and R are letters then.
    const typing = (e.target as HTMLElement | null)?.tagName === 'INPUT';
    if (typing || this.input.captureText) return;
    if (e.code === 'KeyQ') this.snapTurn(-1);
    if (e.code === 'KeyE') this.snapTurn(1);
    if (e.code === 'KeyR') this.recalibrate();
  }

  private clampToBounds(p: THREE.Vector3): THREE.Vector3 {
    const b = this.bounds;
    p.x = THREE.MathUtils.clamp(p.x, b.minX, b.maxX);
    p.z = THREE.MathUtils.clamp(p.z, b.minZ, b.maxZ);
    if (b.circle) {
      const dx = p.x - b.circle.x;
      const dz = p.z - b.circle.z;
      const d = Math.hypot(dx, dz);
      if (d > b.circle.r) {
        p.x = b.circle.x + (dx / d) * b.circle.r;
        p.z = b.circle.z + (dz / d) * b.circle.r;
      }
    }
    if (b.polygon) {
      const q = clampToPolygon(p, b.polygon.points, b.polygon.margin);
      p.x = q.x;
      p.z = q.z;
    }
    for (const r of b.blocked ?? []) {
      if (p.x > r.minX && p.x < r.maxX && p.z > r.minZ && p.z < r.maxZ) {
        // Push out through the nearest edge.
        const dx = Math.min(p.x - r.minX, r.maxX - p.x);
        const dz = Math.min(p.z - r.minZ, r.maxZ - p.z);
        if (dx < dz) p.x = p.x - r.minX < r.maxX - p.x ? r.minX - 0.01 : r.maxX + 0.01;
        else p.z = p.z - r.minZ < r.maxZ - p.z ? r.minZ - 0.01 : r.maxZ + 0.01;
      }
    }
    return p;
  }

  /** Switches seated mode on when the headset stays low, off when the user stands up. */
  private updateSeated(dt: number, inXr: boolean): void {
    if (!inXr) {
      this.lowFor = this.highFor = 0;
      return;
    }
    const tracked = this.rig.camera.position.y;
    // A headset lying on a desk reads near the floor: ignore heights no seated person has.
    const low = this.rig.heightOffset === 0 && tracked > 0.5 && tracked < SEATED.below;
    this.lowFor = low ? this.lowFor + dt : 0;
    if (this.lowFor > SEATED.afterS) {
      this.lowFor = 0;
      this.recalibrate();
      this.autoSeated = this.rig.heightOffset !== 0;
      if (this.autoSeated) this.options.onSeated?.(true);
    }
    const standing = this.autoSeated && tracked + this.rig.heightOffset > SEATED.standAbove;
    this.highFor = standing ? this.highFor + dt : 0;
    if (this.highFor > SEATED.standAfterS) {
      this.highFor = 0;
      this.rig.resetHeight();
      this.autoSeated = false;
      this.options.onSeated?.(false);
    }
  }

  update(dt: number, inXr: boolean): void {
    this.updateSeated(dt, inXr);
    // Desktop mouse look (pointer captured): yaw on the rig, pitch on the camera.
    const look = this.input.consumeLook();
    if (!inXr && (look.x || look.y)) {
      this.rig.group.rotation.y -= look.x * MOUSE_SENSITIVITY;
      const cam = this.rig.camera;
      cam.rotation.x = THREE.MathUtils.clamp(
        cam.rotation.x - look.y * MOUSE_SENSITIVITY,
        -1.4,
        1.4,
      );
    }
    if (!this.enabled) {
      this.vignette.setStrength(0);
      return;
    }
    let smoothMoving = false;

    if (inXr) {
      for (const p of this.input.activePointers()) {
        if (p.kind === 'mouse') continue;
        // Snap turn on the right stick (or any stick when only one controller).
        const useForTurn = p.handedness !== 'left';
        if (useForTurn) {
          const armed = this.snapArmed.get(p.id) ?? true;
          if (armed && Math.abs(p.axes.x) > SNAP_THRESHOLD && Math.abs(p.axes.y) < 0.5) {
            this.snapTurn(p.axes.x > 0 ? 1 : -1);
            this.snapArmed.set(p.id, false);
          } else if (Math.abs(p.axes.x) < 0.3) {
            this.snapArmed.set(p.id, true);
          }
          // Thumbstick forward starts teleport aiming; release commits.
          const stickAim = this.stickAiming.get(p.id) ?? false;
          if (!stickAim && p.axes.y < -SNAP_THRESHOLD && !this.aiming) {
            this.stickAiming.set(p.id, true);
            this.startAim(p);
          } else if (stickAim && p.axes.y > -0.3) {
            this.stickAiming.set(p.id, false);
            if (this.aiming === p) this.commitTeleport();
          }
        }
        // Smooth locomotion on the left stick when enabled.
        if (
          this.options.smoothLocomotion &&
          p.handedness === 'left' &&
          (Math.abs(p.axes.x) > 0.15 || Math.abs(p.axes.y) > 0.15)
        ) {
          const forward = this.rig.headForward(this.tmpDir);
          const right = new THREE.Vector3(-forward.z, 0, forward.x);
          this.tmp
            .set(0, 0, 0)
            .addScaledVector(forward, -p.axes.y)
            .addScaledVector(right, p.axes.x);
          this.moveFeet(this.tmp.multiplyScalar(this.options.walkSpeed * dt));
          smoothMoving = true;
        }
        // B/Y recalibrates seated height.
        if (p.buttons.b) {
          if (this.recalibrateArmed.get(p.id) ?? true) {
            this.recalibrate();
            this.recalibrateArmed.set(p.id, false);
          }
        } else {
          this.recalibrateArmed.set(p.id, true);
        }
      }
      if (this.aiming) this.updateArc(this.aiming);
    } else {
      // Desktop WASD.
      const keys = this.input.keysDown;
      const fwd =
        (keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0) -
        (keys.has('KeyS') || keys.has('ArrowDown') ? 1 : 0);
      const str =
        (keys.has('KeyD') || keys.has('ArrowRight') ? 1 : 0) -
        (keys.has('KeyA') || keys.has('ArrowLeft') ? 1 : 0);
      if (fwd || str) {
        const forward = this.rig.headForward(this.tmpDir);
        const right = new THREE.Vector3(-forward.z, 0, forward.x);
        const boost = keys.has('ShiftLeft') || keys.has('ShiftRight') ? 2 : 1;
        this.tmp.set(0, 0, 0).addScaledVector(forward, fwd).addScaledVector(right, str).normalize();
        this.moveFeet(this.tmp.multiplyScalar(this.options.walkSpeed * boost * dt));
        smoothMoving = true;
      }
    }

    this.moving = smoothMoving
      ? Math.min(1, this.moving + dt * 4)
      : Math.max(0, this.moving - dt * 3);
    this.vignette.setStrength(inXr ? this.moving * 0.75 : 0);
  }

  private moveFeet(delta: THREE.Vector3): void {
    const feet = this.rig.feetPosition(new THREE.Vector3()).add(delta);
    const clamped = this.clampToBounds(feet);
    const current = this.rig.feetPosition(new THREE.Vector3());
    this.rig.group.position.x += clamped.x - current.x;
    this.rig.group.position.z += clamped.z - current.z;
  }

  private updateArc(p: Pointer): void {
    // Ballistic arc from the pointer; lands on the first floor hit.
    const points = this.arc.geometry.getAttribute('position') as THREE.BufferAttribute;
    const origin = p.ray.origin.clone();
    const velocity = p.ray.direction.clone().multiplyScalar(7);
    let hit: THREE.Vector3 | undefined;
    const step = 0.06;
    let prev = origin.clone();
    let i = 0;
    for (let t = step; i < points.count; t += step, i++) {
      const next = origin.clone().addScaledVector(velocity, t);
      next.y += 0.5 * GRAVITY * t * t;
      this.raycaster.set(prev, next.clone().sub(prev).normalize());
      this.raycaster.far = prev.distanceTo(next);
      const hits = this.raycaster.intersectObjects(this.floors, true);
      if (hits.length > 0) {
        hit = hits[0]!.point;
        points.setXYZ(i, hit.x, hit.y, hit.z);
        i++;
        break;
      }
      points.setXYZ(i, next.x, next.y, next.z);
      prev = next;
      if (next.y < -2) break;
    }
    for (; i < points.count; i++) points.setXYZ(i, prev.x, prev.y, prev.z);
    points.needsUpdate = true;
    this.arc.visible = true;
    if (hit && origin.distanceTo(hit) <= TELEPORT_MAX_DISTANCE) {
      const clamped = this.clampToBounds(hit.clone());
      this.aimTarget = clamped;
      this.marker.position.set(clamped.x, hit.y + 0.01, clamped.z);
      this.marker.visible = true;
      (this.marker.material as THREE.MeshBasicMaterial).color.set(0x35b5b0);
    } else {
      this.aimTarget = undefined;
      this.marker.visible = false;
    }
  }
}
